import {
  type BufferGeometry,
  CanvasTexture,
  LinearFilter,
  type MeshBasicMaterial,
  Sprite,
  SpriteMaterial,
  SRGBColorSpace,
} from 'three';
import {StandardViewType, type StandardViewType as StandardViewTypeValue,} from '../view-orientation/standardViewTypes';
import type {ViewOrientationMesh} from '../view-orientation/ViewOrientationMesh';


/** 集中创建并释放方向立方体中文标签和坐标轴文字纹理。 */
export class ViewportGizmoLabelTextureFactory {
  /** 需要显示中文名称的六个主视图。 */
  private static readonly FACE_VIEW_TYPES: readonly StandardViewTypeValue[] = [
    StandardViewType.Front,
    StandardViewType.Back,
    StandardViewType.Left,
    StandardViewType.Right,
    StandardViewType.Top,
    StandardViewType.Bottom,
  ];

  /** 当前工厂创建的全部纹理。 */
  private readonly textures = new Set<CanvasTexture>();

  /**
   * 将中文方向标签纹理应用到六个主面。
   *
   * @param mesh 导航器中央方向立方体。
   */
  public applyFaceLabels(mesh: ViewOrientationMesh): void {
    for (const viewType of ViewportGizmoLabelTextureFactory.FACE_VIEW_TYPES) {
      const element = mesh.getMeshByViewType(viewType);
      if (!element) {
        continue;
      }

      // ShapeGeometry 默认输出世界坐标 UV，必须先映射到纹理标准范围才能采样到中央文字。
      this.normalizeFaceTextureCoordinates(element.geometry);

      // 主面标签移除“视”后缀，使短文字在小尺寸导航器中保持清晰。
      const texture = this.createTexture(
        element.label.replace('视', ''),
        '#333333',
        'rgba(240, 240, 240, 0.96)',
        128,
        62,
      );
      this.applyTextureToMaterial(element.material, texture);
    }
  }

  /**
   * 将主面局部 XY 坐标映射为 `[0, 1]` 范围的纹理坐标。
   *
   * Three.js `ShapeGeometry` 直接把局部世界坐标写入 UV。主面尺寸大于 1 时，
   * 默认钳制采样只会读取纹理边缘，位于画布中心的方向文字因此完全不可见。
   *
   * @param geometry 需要显示方向标签的单个主面几何。
   * @throws {Error} 几何缺少位置或 UV 属性、属性数量不一致时抛出。
   * @throws {RangeError} 主面宽高退化为零时抛出。
   */
  private normalizeFaceTextureCoordinates(geometry: BufferGeometry): void {
    const position = geometry.getAttribute('position');
    const uv = geometry.getAttribute('uv');
    if (!position || !uv) {
      throw new Error('视口方向主面必须同时包含 position 与 uv 属性。');
    }
    if (position.count !== uv.count) {
      throw new Error('视口方向主面的 position 与 uv 顶点数量必须一致。');
    }

    let minimumX = Number.POSITIVE_INFINITY;
    let maximumX = Number.NEGATIVE_INFINITY;
    let minimumY = Number.POSITIVE_INFINITY;
    let maximumY = Number.NEGATIVE_INFINITY;

    // 只读取局部 XY 平面范围，不依赖对象在六个世界轴方向上的装配姿态。
    for (let index = 0; index < position.count; index += 1) {
      const x = position.getX(index);
      const y = position.getY(index);
      minimumX = Math.min(minimumX, x);
      maximumX = Math.max(maximumX, x);
      minimumY = Math.min(minimumY, y);
      maximumY = Math.max(maximumY, y);
    }

    const width = maximumX - minimumX;
    const height = maximumY - minimumY;
    if (width <= Number.EPSILON || height <= Number.EPSILON) {
      throw new RangeError('视口方向主面必须具有有效的局部宽度和高度。');
    }

    // 使用几何位置重新生成归一化 UV，确保纹理中心文字落在主面中心。
    for (let index = 0; index < position.count; index += 1) {
      uv.setXY(
        index,
        (position.getX(index) - minimumX) / width,
        (position.getY(index) - minimumY) / height,
      );
    }
    uv.needsUpdate = true;
  }

  /**
   * 创建始终朝向导航器相机的坐标轴文字 Sprite。
   *
   * @param text X、Y 或 Z。
   * @param color 坐标轴语义颜色。
   * @returns 已配置透明纹理的 Sprite。
   */
  public createAxisLabel(text: string, color: string): Sprite {
    const texture = this.createTexture(
      text,
      color,
      'rgba(0, 0, 0, 0)',
      64,
      42,
    );
    const material = new SpriteMaterial({
      map: texture,
      transparent: true,
      depthTest: false,
      depthWrite: false,
    });
    const sprite = new Sprite(material);
    sprite.scale.set(12, 12, 1);
    sprite.renderOrder = 72;
    return sprite;
  }

  /** 释放全部 CanvasTexture。 */
  public dispose(): void {
    for (const texture of this.textures) {
      texture.dispose();
    }
    this.textures.clear();
  }

  /**
   * 将纹理设置到主面材质并保持标签为实色显示。
   *
   * @param material 目标主面材质。
   * @param texture 已绘制标签的纹理。
   */
  private applyTextureToMaterial(
    material: MeshBasicMaterial,
    texture: CanvasTexture,
  ): void {
    material.map = texture;
    material.opacity = 1;
    material.transparent = false;
    material.depthWrite = true;
    material.needsUpdate = true;
  }

  /**
   * 创建单个高分辨率文字纹理。
   *
   * @param text 绘制文字。
   * @param foregroundColor 文字颜色。
   * @param backgroundColor 背景颜色。
   * @param resolution 纹理宽高分辨率。
   * @param fontSize 字体像素大小。
   * @returns 已登记生命周期的画布纹理。
   */
  private createTexture(
    text: string,
    foregroundColor: string,
    backgroundColor: string,
    resolution: number,
    fontSize: number,
  ): CanvasTexture {
    const canvas = document.createElement('canvas');
    canvas.width = resolution;
    canvas.height = resolution;

    // 无 Canvas2D 的测试或服务端环境仍创建空纹理，真实浏览器再绘制完整标签。
    let context: CanvasRenderingContext2D | null = null;
    if (typeof globalThis.CanvasRenderingContext2D !== 'undefined') {
      try {
        context = canvas.getContext('2d');
      } catch (error) {
        console.warn(
          '[ViewportGizmoLabelTextureFactory] 无法创建 Canvas2D 标签上下文，将使用空纹理。',
          error,
        );
        context = null;
      }
    }

    if (context) {
      context.clearRect(0, 0, resolution, resolution);
      context.fillStyle = backgroundColor;
      context.fillRect(0, 0, resolution, resolution);
      context.fillStyle = foregroundColor;
      context.font = `700 ${fontSize}px sans-serif`;
      context.textAlign = 'center';
      context.textBaseline = 'middle';
      context.fillText(text, resolution / 2, resolution / 2);
    }

    const texture = new CanvasTexture(canvas);
    texture.colorSpace = SRGBColorSpace;
    texture.minFilter = LinearFilter;
    texture.magFilter = LinearFilter;
    texture.needsUpdate = true;
    this.textures.add(texture);
    return texture;
  }
}

